/**
 * Notes.
 *
 * Built against taskdojo-app's PersonalNotesScreen and notesService. Notes live
 * in the global `notes` collection keyed by `userId`, exactly as tasks and
 * habits do.
 *
 * The document shape is the phone's, field for field, because both platforms
 * read it:
 *
 *     { title, content, isFavorite, reminder?, reminderSent, userId,
 *       createdAt, updatedAt }
 *
 * Two details that are easy to get wrong and would show up on the phone:
 *
 *  - `reminderSent` is written false on create whether or not there is a
 *    reminder -- the phone does that deliberately, "for consistency". On edit
 *    it is touched ONLY when the reminder actually changes. The scheduler
 *    collects anything with a reminder and reminderSent == false, so resetting
 *    the flag on every save would re-fire a reminder that had already gone.
 *  - notes use addDoc, so Firestore assigns the id and the document does not
 *    carry one. Writing an `id` field here would be harmless but divergent.
 *
 * Favourited notes are what the phone's Dashboard "Pinned notes" widget reads,
 * so the star is not decoration -- it decides what shows up there.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ScreenHeader, SummaryBand, ActionButton, PrimaryButton, SecondaryButton,
  Field, useInputClass, raised, pressable,
} from '../dojo';
import { formatRelative, formatDateTime, toDateInput, toTimeInput, fromDateTimeInputs } from '../../utils/datetime';

interface Note {
  id: string;
  title: string;
  content: string;
  isFavorite: boolean;
  reminder?: string | null;
  reminderSent?: boolean;
  userId: string;
  createdAt: any;
  updatedAt: any;
}

const PersonalNotes: React.FC = () => {
  const { state } = useApp();
  const { colors, isDark } = useTheme();
  const input = useInputClass();

  const uid = state.user?.uid;

  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Note | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Note | null>(null);

  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'notes'), where('userId', '==', uid));
    const unsub = onSnapshot(q, (snap) => {
      const out: Note[] = [];
      snap.forEach((d) => out.push({ ...(d.data() as Note), id: d.id }));
      // Favourites first, then most recently touched. Sorted here rather than
      // in the query: an orderBy would drop any note missing the field.
      out.sort((a, b) => {
        if (!!a.isFavorite !== !!b.isFavorite) return a.isFavorite ? -1 : 1;
        const at = a.updatedAt?.toMillis?.() ?? 0;
        const bt = b.updatedAt?.toMillis?.() ?? 0;
        return bt - at;
      });
      setNotes(out);
      setLoading(false);
    }, (e) => {
      setError(e?.message || "Couldn't load your notes.");
      setLoading(false);
    });
    return unsub;
  }, [uid]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) =>
      (n.title || '').toLowerCase().includes(q) || (n.content || '').toLowerCase().includes(q));
  }, [notes, search]);

  const favourites = notes.filter((n) => n.isFavorite).length;
  const withReminders = notes.filter((n) => !!n.reminder).length;

  const openCreate = () => {
    setTitle(''); setContent(''); setReminderDate(''); setReminderTime('');
    setEditing(null); setCreating(true); setError('');
  };

  const openEdit = (n: Note) => {
    setTitle(n.title || '');
    setContent(n.content || '');
    setReminderDate(n.reminder ? toDateInput(n.reminder) : '');
    setReminderTime(n.reminder ? toTimeInput(n.reminder) : '');
    setEditing(n); setCreating(false); setError('');
  };

  const closeModal = () => { setEditing(null); setCreating(false); setError(''); };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uid) return;
    // The phone's rule: a note needs one or the other, not both.
    if (!title.trim() && !content.trim()) {
      setError('Give the note a title or something to say.');
      return;
    }
    setSaving(true); setError('');
    try {
      const reminder = reminderDate ? fromDateTimeInputs(reminderDate, reminderTime) : null;

      if (editing) {
        const patch: Record<string, any> = {
          title: title.trim() || 'Untitled',
          content: content.trim(),
          updatedAt: serverTimestamp(),
        };

        // Only touch the reminder when it actually changed, which is what the
        // phone's notesService does. Writing reminderSent:false on every edit
        // re-arms the reminder: the scheduler picks up anything with a reminder
        // and reminderSent == false, so renaming a note whose reminder had
        // already fired would fire it again, on the phone.
        const before = editing.reminder || null;
        if (reminder !== before) {
          patch.reminder = reminder;
          patch.reminderSent = false;
        }

        await updateDoc(doc(db, 'notes', editing.id), patch);
      } else {
        await addDoc(collection(db, 'notes'), {
          title: title.trim() || 'Untitled',
          content: content.trim(),
          isFavorite: false,
          ...(reminder ? { reminder } : {}),
          reminderSent: false,
          userId: uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Couldn't save that note.");
    } finally {
      setSaving(false);
    }
  };

  const toggleFavourite = async (n: Note) => {
    if (busy) return;
    setBusy(n.id);
    try {
      await updateDoc(doc(db, 'notes', n.id), {
        isFavorite: !n.isFavorite,
        updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      setError(err?.message || "Couldn't save that.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (n: Note) => {
    setSaving(true);
    try {
      await deleteDoc(doc(db, 'notes', n.id));
      setConfirmDelete(null);
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Couldn't delete that note.");
    } finally {
      setSaving(false);
    }
  };

  const modalOpen = creating || !!editing;

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader
          title="Notes"
          action={
            <ActionButton label="New note" onClick={openCreate}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </ActionButton>
          }
        />

        <SummaryBand
          items={[
            { value: notes.length, label: 'Notes' },
            { value: favourites, label: 'Pinned', color: isDark ? '#FEC200' : '#C08A1E' },
            { value: withReminders, label: 'Reminders', color: '#4A6FA5' },
          ]}
        />

        {notes.length > 0 && (
          <div className="px-5 mb-4">
            <input
              className={input}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search your notes"
              type="search"
            />
          </div>
        )}

        {error && !modalOpen && (
          <div role="alert" className="mx-5 mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
            px-4 py-3 text-[13px] font-semibold text-dojo-red">
            {error}
          </div>
        )}

        <div className="px-5">
          {loading ? (
            <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Loading…</p>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center text-center py-20">
              <div className={`w-[88px] h-[88px] rounded-[30px] border-2 ${colors.cardBorder} ${colors.card}
                flex items-center justify-center mb-[18px] text-[34px]`}>📓</div>
              <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">
                {search ? 'Nothing matches' : 'No notes yet'}
              </h2>
              <p className={`text-[15px] font-semibold mb-5 ${colors.textSecondary}`}>
                {search ? 'Try a different word.' : 'Write down the thing you keep forgetting.'}
              </p>
              {!search && (
                <button
                  type="button"
                  onClick={openCreate}
                  className={`rounded-[14px] bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}
                    px-5 pt-[13px] pb-[11px] text-[15px] font-extrabold`}
                >
                  Write your first note
                </button>
              )}
            </div>
          ) : (
            <ul className="grid sm:grid-cols-2 gap-3 list-none p-0 m-0">
              {visible.map((n) => (
                <li key={n.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => openEdit(n)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(n); }
                    }}
                    className={`h-full rounded-[15px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder}
                      p-4 flex flex-col cursor-pointer transition-colors
                      focus:outline-none focus-visible:border-dojo-red
                      ${isDark ? 'hover:border-slate-500' : 'hover:border-stone-400'}`}
                  >
                    <div className="flex items-start gap-2 mb-1.5">
                      <h3 className="flex-1 min-w-0 text-[16px] font-extrabold tracking-[-0.2px] line-clamp-2">
                        {n.title || 'Untitled'}
                      </h3>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleFavourite(n); }}
                        disabled={busy === n.id}
                        aria-pressed={!!n.isFavorite}
                        aria-label={n.isFavorite ? `Unpin ${n.title}` : `Pin ${n.title}`}
                        className="flex-shrink-0 disabled:opacity-50"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24"
                          fill={n.isFavorite ? '#E8B33A' : 'none'}
                          stroke={n.isFavorite ? '#E8B33A' : 'currentColor'}
                          strokeWidth="2"
                          className={n.isFavorite ? '' : colors.textMuted}>
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z"
                            strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>

                    {n.content && (
                      <p className={`text-[13.5px] leading-snug line-clamp-4 flex-1 ${colors.textSecondary}`}>
                        {n.content}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3">
                      <span className={`text-[11.5px] font-semibold ${colors.textMuted}`}>
                        {n.updatedAt ? formatRelative(n.updatedAt) : ''}
                      </span>
                      {n.reminder && (
                        <span className="flex items-center gap-1 text-[11.5px] font-semibold text-dojo-blue">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2.5">
                            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0"
                              strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {formatDateTime(n.reminder)}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={closeModal}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-lg max-h-[92vh] overflow-y-auto ${colors.card}
              border-2 ${colors.cardBorder} rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <div className="flex items-center gap-3 mb-5">
              <h2 className="text-[26px] font-black tracking-[-0.7px] flex-1">
                {editing ? 'Edit note' : 'New note'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                aria-label="Close"
                className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.textSecondary}`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <Field label="Title" className="mb-4">
              <input
                className={input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Untitled"
                autoFocus
              />
            </Field>

            <Field label="Note" className="mb-4">
              <textarea
                className={`${input} min-h-[180px] resize-y leading-relaxed`}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write it down…"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3 mb-2">
              <Field label="Remind me">
                <input
                  type="date"
                  className={input}
                  value={reminderDate}
                  onChange={(e) => setReminderDate(e.target.value)}
                />
              </Field>
              <Field label="Time">
                <input
                  type="time"
                  className={input}
                  value={reminderTime}
                  disabled={!reminderDate}
                  onChange={(e) => setReminderTime(e.target.value)}
                />
              </Field>
            </div>
            <p className={`text-[12px] font-semibold mb-5 ${colors.textSecondary}`}>
              {reminderDate
                ? `Reminding you ${formatDateTime(fromDateTimeInputs(reminderDate, reminderTime))} — delivered by the taskDojo app.`
                : 'Optional. Reminders are delivered by the taskDojo app.'}
            </p>

            {error && (
              <div role="alert" className="mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
                px-4 py-3 text-[13px] font-semibold text-dojo-red">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <PrimaryButton type="submit" disabled={saving} className="flex-1">
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create note'}
              </PrimaryButton>
              {editing && (
                <SecondaryButton
                  onClick={() => setConfirmDelete(editing)}
                  disabled={saving}
                  className="!text-dojo-red"
                >
                  Delete
                </SecondaryButton>
              )}
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
          <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
            <h3 className="text-xl font-black tracking-[-0.4px] mb-2">Delete this note?</h3>
            <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
              “{confirmDelete.title || 'Untitled'}” will be removed. This can't be undone.
            </p>
            <div className="flex gap-3">
              <SecondaryButton onClick={() => setConfirmDelete(null)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton onClick={() => remove(confirmDelete)} disabled={saving} className="flex-1">
                {saving ? 'Deleting…' : 'Delete'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalNotes;
